import { Button, Drawer, Flex, Text, useMantineTheme } from '@mantine/core';
import { Select } from 'react-hook-form-mantine';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import {
	DEAL_ROUTES,
	PEOPLE_ROUTES,
	ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { BankAccountRes } from '@dealstack/shared/lib/generated';
import { setInvestmentSelectedBankAccount } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service.ts';
import { updateKycWorkflowKycSetting } from '@dealstack/frontend/shared/dataExchange/services/kyc-setting.service.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { ManageBankFormValues } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/ManageBankDrawer/ManageBankDrawer.types.ts';
import { ManageBankDrawerStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/ManageBankDrawer/ManageBankDrawer.styles.tsx';
import { BankAccountTile } from '@dealstack/frontend/shared/components/BankAccountTile/BankAccountTile.tsx';
import { getBankAccountOptionLabel } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/ManageBankDrawer/ManageBankDrawer.utils.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';

import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox.tsx';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';

interface Props {
	opened: boolean;
	onClose: () => void;
	onSaved: () => void;
	personId: string | null;
	selectedBankAccountId: string | null;
	bankAccounts: BankAccountRes[];
	isLoading?: boolean;
	kycWorkflowId: string | undefined;
	isBankAccountRequired: boolean;
	isPivInvestment: boolean;
}

const schema = z
	.object({
		bankAccountId: z.string(),
		withBank: z.boolean(),
	})
	.refine((values) => !values.withBank || values.bankAccountId.length > 0, {
		path: ['bankAccountId'],
		message: 'Select a bank account',
	});

export const ManageBankDrawer = ({
	opened,
	onClose,
	onSaved,
	personId,
	selectedBankAccountId,
	bankAccounts,
	isLoading = false,
	kycWorkflowId,
	isBankAccountRequired,
	isPivInvestment,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const navigate = useNavigate();
	const { dealId, mipId, investmentId } = useRequiredParams('dealId', 'mipId', 'investmentId');
	const mantineTheme = useMantineTheme();

	const bankAccountOptions = useMemo(
		() =>
			bankAccounts.map((account) => ({
				value: account.id,
				label: getBankAccountOptionLabel(account),
			})),
		[bankAccounts]
	);

	const form = useForm<ManageBankFormValues>({
		resolver: zodResolver(schema),
		defaultValues: {
			bankAccountId: selectedBankAccountId ?? '',
			withBank: isBankAccountRequired,
		},
	});

	const bankToggled = form.watch('withBank');
	const pickedBankAccountId = form.watch('bankAccountId');

	useEffect(() => {
		if (opened) {
			form.reset({
				bankAccountId: selectedBankAccountId ?? '',
				withBank: isBankAccountRequired,
			});
		}
	}, [opened, selectedBankAccountId, isBankAccountRequired, form]);

	const saveMutation = useMutation({
		mutationFn: async (values: ManageBankFormValues) => {
			const targetBankAccountId =
				values.withBank && values.bankAccountId ? values.bankAccountId : null;

			if (kycWorkflowId && values.withBank !== isBankAccountRequired) {
				await updateKycWorkflowKycSetting(
					kycWorkflowId,
					isPivInvestment
						? { isVehicleBankAccountRequired: values.withBank }
						: { isInvestorBankAccountRequired: values.withBank }
				);
			}

			await setInvestmentSelectedBankAccount({
				mipId,
				investmentId,
				payload: { bankAccountId: targetBankAccountId },
			});
		},
		onSuccess: () => {
			showToastV2({
				title: tMipManage('manage_investment_bank_drawer.save_success', {
					defaultValue: 'Bank information updated',
				}),
			});
			onSaved();
			onClose();
		},
		onError: () => {
			showToastV2({
				state: 'error',
				title: tMipManage('manage_investment_bank_drawer.save_error', {
					defaultValue: 'Failed to update bank information',
				}),
			});
		},
	});

	const onSubmit = (values: ManageBankFormValues) => {
		saveMutation.mutate(values);
	};

	const toggleBank = (toggled: boolean) => {
		form.setValue('withBank', toggled);

		if (!toggled) {
			form.setValue('bankAccountId', '');
		}
	};

	const pickedBankAccount = useMemo(
		() => bankAccounts.find((account) => account.id === pickedBankAccountId) ?? null,
		[bankAccounts, pickedBankAccountId]
	);
	const isCurrentSelection =
		!!selectedBankAccountId && selectedBankAccountId === pickedBankAccount?.id;
	const showBankBox = bankToggled && !!pickedBankAccount;

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			size="484px"
			title={tMipManage('manage_investment_bank_drawer.title', {
				defaultValue: 'Bank information',
			})}
			transitionProps={drawerTransitionProps}
			trapFocus={false}
		>
			<ManageBankDrawerStyles>
				<FormProvider {...form}>
					<form style={{ height: '100%' }} onSubmit={form.handleSubmit(onSubmit)}>
						<Flex direction="column" h="100%">
							<Flex justify="space-between" align="flex-start" gap="1rem" mb="1rem">
								<Flex direction="column" gap="0.25rem">
									<Text fw={600} c="dark.8">
										{tMipManage('manage_investment_bank_drawer.toggle.label', {
											defaultValue: 'Bank account details',
										})}
									</Text>

									<Text c="dark.0">
										{tMipManage(
											'manage_investment_bank_drawer.toggle.description',
											{
												defaultValue:
													'Toggle whether bank info is mandatory for this investment.',
											}
										)}
									</Text>
								</Flex>

								<AdvancedSwitch
									selectedValue={bankToggled}
									onChange={(event) => toggleBank(!!event)}
									size="m"
								/>
							</Flex>

							<Select
								name="bankAccountId"
								label={tMipManage('manage_investment_bank_drawer.select.label', {
									defaultValue: 'Select bank account',
								})}
								placeholder={tMipManage(
									'manage_investment_bank_drawer.select.placeholder',
									{
										defaultValue: 'Select',
									}
								)}
								data={bankAccountOptions}
								searchable
								size="md"
								disabled={!bankToggled}
								styles={{
									root: {
										marginBottom: '1rem',
										opacity: bankToggled ? 1 : 0.5,
									},
								}}
							/>

							{!isLoading && !bankAccountOptions.length && (
								<div className="options-warning">
									<InfoBox
										iconVariant="warning"
										title={tMipManage(
											'manage_investment_bank_drawer.options.alert.title',
											{
												defaultValue: 'No available bank accounts',
											}
										)}
										description={tMipManage(
											'manage_investment_bank_drawer.options.alert.description',
											{
												defaultValue:
													'There are no available bank accounts for this person. You can create a new one in the Deal Directory through the button below.',
											}
										)}
									/>
								</div>
							)}

							{showBankBox && pickedBankAccount && (
								<BankAccountTile
									bankAccount={pickedBankAccount}
									hasEditPermission={false}
									withBorder
									showStatusBadge
									backgroundColor={mantineTheme.colors.gray[0]}
									customContent={
										isCurrentSelection ? (
											<Text className="current-pill" fz="sm" c="dark.4">
												{tMipManage(
													'manage_investment_bank_drawer.current_pill'
												)}
											</Text>
										) : undefined
									}
									tileTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTMENT_BANK_TILE}
									testIdSuffix={pickedBankAccount.id}
								/>
							)}

							<Flex justify="flex-start" mt="1rem">
								<Button
									fw={600}
									variant="subtle"
									disabled={!personId}
									onClick={() => {
										if (!personId) {
											return;
										}

										navigate({
											pathname: generatePath(
												`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.PERSON_DETAILS}`,
												{
													dealId,
													personId,
												}
											),
										});
									}}
								>
									<Text mr="1rem" color="blue.6">
										{tMipManage('manage_investment_bank_drawer.view_button', {
											defaultValue: 'Go to directory',
										})}
									</Text>

									<Icon
										variant="arrow-up-right-2"
										size="md"
										color={mantineTheme.colors.blue[6]}
										fillStroke={true}
									/>
								</Button>
							</Flex>

							<Text c="dark.9" mt="0.25rem">
								{tMipManage('manage_investment_bank_drawer.more_label', {
									defaultValue:
										'Create a new Bank Account record in the Deal Directory to make it available for selection.',
								})}
							</Text>

							<Flex direction="column" mt="auto" gap="1rem">
								<Button
									type="submit"
									fw={600}
									fullWidth
									loading={saveMutation.isPending}
								>
									{tMipManage('manage_investment_bank_drawer.cta.save', {
										defaultValue: 'Save selection',
									})}
								</Button>

								<Button
									variant="subtle"
									c="dark.8"
									fullWidth
									onClick={onClose}
									disabled={saveMutation.isPending}
								>
									{tMipManage('manage_investment_bank_drawer.cta.cancel', {
										defaultValue: 'Cancel',
									})}
								</Button>
							</Flex>
						</Flex>
					</form>
				</FormProvider>
			</ManageBankDrawerStyles>
		</Drawer>
	);
};
